import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../../utils/apiConfig';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Chip,
  Tooltip,
  Divider,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as TestIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../CentralSnackbar';
import TenantMultiSelect from '../common/TenantMultiSelect';


const CDASendNodes = ({ onNodesChanged }) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    title: '',
    description: '',
    onConfirm: null
  });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    ae_title: '',
    ip_address: '',
    port: '',
    is_active: true,
    tenants: ''
  });

  useEffect(() => {
    fetchNodes();
  }, []);

  const fetchNodes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/cda-send-nodes/`);
      if (response.ok) {
        const data = await response.json();
        setNodes(data);
      } else {
        throw new Error('Failed to fetch CDA send nodes');
      }
    } catch (error) {
      console.error('Error fetching CDA send nodes:', error);
      showError(t('cdaSendNodes.snackbar.loadError', 'Fehler beim Laden der CDA Sendeknoten'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectNode = (nodeId) => {
    setSelectedNodes(prev => 
      prev.includes(nodeId) 
        ? prev.filter(id => id !== nodeId)
        : [...prev, nodeId]
    );
  };

  const handleSelectAll = () => {
    if (selectedNodes.length === nodes.length) {
      setSelectedNodes([]);
    } else {
      setSelectedNodes(nodes.map(node => node.id));
    }
  };

  const handleEditSelected = () => {
    if (selectedNodes.length === 1) {
      const node = nodes.find(n => n.id === selectedNodes[0]);
      handleOpenDialog(node);
    }
  };

  const handleTestSelected = () => {
    if (selectedNodes.length === 1) {
      handleTestConnection(selectedNodes[0]);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedNodes.length === 0) return;
    
    let description;
    if (selectedNodes.length === 1) {
      const selectedNode = nodes.find(node => node.id === selectedNodes[0]);
      description = t('cdaSendNodes.confirmDelete.description', { 
        defaultValue: 'Möchten Sie den CDA Sendeknoten "{{name}}" wirklich löschen?',
        name: selectedNode?.name || 'Unknown' 
      });
    } else {
      description = t('cdaSendNodes.confirmDelete.descriptionMultiple', { 
        defaultValue: 'Möchten Sie {{count}} CDA Sendeknoten wirklich löschen?',
        count: selectedNodes.length 
      });
    }

    setConfirmDelete({
      open: true,
      title: t('cdaSendNodes.confirmDelete.title', 'CDA Sendeknoten löschen'),
      description: description,
      onConfirm: async () => {
        try {
          const deletePromises = selectedNodes.map(nodeId =>
            fetch(`${getApiBaseUrl()}/cda-send-nodes/${nodeId}`, {
              method: 'DELETE'
            })
          );

          const results = await Promise.all(deletePromises);
          const allSuccessful = results.every(response => response.ok);

          if (allSuccessful) {
            showSuccess(selectedNodes.length === 1 
              ? t('cdaSendNodes.snackbar.deleteSuccess', 'CDA Sendeknoten erfolgreich gelöscht')
              : `${selectedNodes.length} CDA Sendeknoten erfolgreich gelöscht`);
            setSelectedNodes([]);
            fetchNodes();
            // Callback an Parent um Status zu aktualisieren
            if (onNodesChanged) onNodesChanged();
          } else {
            throw new Error('Some deletions failed');
          }
        } catch (error) {
          console.error('Error deleting CDA send nodes:', error);
          showError(t('cdaSendNodes.snackbar.deleteError', 'Fehler beim Löschen des CDA Sendeknotens'));
        }
      }
    });
  };

  const handleOpenDialog = (node = null) => {
    if (node) {
      setEditingNode(node);
      setFormData({
        name: node.name,
        ae_title: node.ae_title,
        ip_address: node.ip_address,
        port: node.port.toString(),
        is_active: node.is_active,
        tenants: node.tenants || ''
      });
    } else {
      setEditingNode(null);
      setFormData({
        name: '',
        ae_title: '',
        ip_address: '',
        port: '',
        is_active: true,
        tenants: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingNode(null);
    setFormData({
      name: '',
      ae_title: '',
      ip_address: '',
      port: '',
      is_active: true,
      tenants: ''
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      const url = editingNode 
        ? `${getApiBaseUrl()}/cda-send-nodes/${editingNode.id}`
        : `${getApiBaseUrl()}/cda-send-nodes/`;
      
      const method = editingNode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          port: parseInt(formData.port)
        })
      });

      if (response.ok) {
        showSuccess(editingNode 
          ? t('cdaSendNodes.snackbar.updateSuccess', 'CDA Sendeknoten erfolgreich aktualisiert')
          : t('cdaSendNodes.snackbar.createSuccess', 'CDA Sendeknoten erfolgreich erstellt'));
        handleCloseDialog();
        fetchNodes();
        // Callback an Parent um Status zu aktualisieren
        if (onNodesChanged) onNodesChanged();
      } else {
        throw new Error('Failed to save CDA send node');
      }
    } catch (error) {
      console.error('Error saving CDA send node:', error);
      showError(t('cdaSendNodes.snackbar.saveError', 'Fehler beim Speichern des CDA Sendeknotens'));
    }
  };

  const handleTestConnection = async (nodeId) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/cda-send-nodes/${nodeId}/test`, {
        method: 'POST'
      });

      if (response.ok) {
        const result = await response.json();
        
        const node = nodes.find(n => n.id === nodeId);
        if (result.success) {
          showSuccess(t('cdaSendNodes.testSuccess', {
            defaultValue: 'CDA-Verbindung zu "{{nodeName}}" erfolgreich getestet',
            nodeName: node?.name || 'Unbekannt'
          }));
        } else {
          showError(t('cdaSendNodes.testFailed', {
            defaultValue: 'ERR-CDA-001: CDA-Verbindungstest zu "{{nodeName}}" fehlgeschlagen: {{error}}',
            nodeName: node?.name || 'Unbekannt',
            error: result.message
          }), 'ERR-CDA-001');
        }
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error testing CDA connection:', error);
      
      const node = nodes.find(n => n.id === nodeId);
      showError(t('cdaSendNodes.testError', {
        defaultValue: 'ERR-CDA-002: Fehler beim Testen der CDA-Verbindung zu "{{nodeName}}"',
        nodeName: node?.name || 'Unbekannt'
      }), 'ERR-CDA-002');
    }
  };

  return (
    <Box>
      {/* Action Buttons - Always visible */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="subtitle2" sx={{ mr: 2 }}>
            {selectedNodes.length > 0 
              ? t('cdaSendNodes.selectedNodes', { 
                  defaultValue: '{{count}} Knoten ausgewählt',
                  count: selectedNodes.length 
                })
              : t('cdaSendNodes.noNodesSelected', 'Keine Knoten ausgewählt')
            }
          </Typography>
          <Divider orientation="vertical" flexItem />
          <Tooltip title={t('cdaSendNodes.actions.edit', 'Bearbeiten')}>
            <IconButton 
              size="small" 
              onClick={handleEditSelected}
              disabled={loading || selectedNodes.length !== 1}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('cdaSendNodes.testConnection', 'Verbindung testen')}>
            <IconButton 
              size="small" 
              onClick={handleTestSelected}
              disabled={loading || selectedNodes.length !== 1}
            >
              <TestIcon />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem />
          <Tooltip title={t('cdaSendNodes.actions.delete', 'Löschen')}>
            <IconButton 
              size="small" 
              onClick={handleDeleteSelected}
              disabled={loading || selectedNodes.length === 0}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ ml: 2, mr: 1 }} />
          <Box display="flex" alignItems="center" gap={1} flexGrow={1} justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              className="button"
            >
              {t('cdaSendNodes.add', 'Hinzufügen')}
            </Button>
          </Box>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedNodes.length === nodes.length && nodes.length > 0}
                  indeterminate={selectedNodes.length > 0 && selectedNodes.length < nodes.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>{t('cdaSendNodes.table.name', 'Name')}</TableCell>
              <TableCell>{t('cdaSendNodes.table.aeTitle', 'AE Title')}</TableCell>
              <TableCell>{t('cdaSendNodes.table.ipAddress', 'IP-Adresse')}</TableCell>
              <TableCell>{t('cdaSendNodes.table.port', 'Port')}</TableCell>
              <TableCell>{t('cdaSendNodes.table.status', 'Status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {nodes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {t('cdaSendNodes.noNodes', 'Keine CDA Sendeknoten konfiguriert')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              nodes.map((node) => (
                <TableRow key={node.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedNodes.includes(node.id)}
                      onChange={() => handleSelectNode(node.id)}
                    />
                  </TableCell>
                  <TableCell>{node.name}</TableCell>
                  <TableCell>{node.ae_title}</TableCell>
                  <TableCell>{node.ip_address}</TableCell>
                  <TableCell>{node.port}</TableCell>
                  <TableCell>
                    <Chip
                      label={node.is_active ? t('cdaSendNodes.status.active', 'Aktiv') : t('cdaSendNodes.status.inactive', 'Inaktiv')}
                      color={node.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingNode ? t('cdaSendNodes.dialog.editTitle', 'CDA Sendeknoten bearbeiten') : t('cdaSendNodes.dialog.createTitle', 'Neuen CDA Sendeknoten erstellen')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('cdaSendNodes.form.name', 'Name')}
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            margin="normal"
            required
            helperText={t('cdaSendNodes.form.nameHelp', 'z.B. "ELGA PACS Node"')}
          />
          <TextField
            fullWidth
            label={t('cdaSendNodes.form.aeTitle', 'AE Title / System Name')}
            value={formData.ae_title}
            onChange={(e) => handleInputChange('ae_title', e.target.value)}
            margin="normal"
            required
            helperText={t('cdaSendNodes.form.aeTitleHelp', 'z.B. "PACS_ELGA"')}
          />
          <TextField
            fullWidth
            label={t('cdaSendNodes.form.ipAddress', 'IP-Adresse')}
            value={formData.ip_address}
            onChange={(e) => handleInputChange('ip_address', e.target.value)}
            margin="normal"
            required
            helperText={t('cdaSendNodes.form.ipAddressHelp', 'z.B. "192.168.1.100"')}
          />
          <TextField
            fullWidth
            label={t('cdaSendNodes.form.port', 'Port')}
            type="number"
            value={formData.port}
            onChange={(e) => handleInputChange('port', e.target.value)}
            margin="normal"
            required
            inputProps={{ min: 1, max: 65535 }}
            helperText={t('cdaSendNodes.form.portHelp', 'z.B. "3378" für CDA')}
          />
          <TenantMultiSelect
            value={formData.tenants}
            onChange={(value) => handleInputChange('tenants', value)}
            label={t('cdaSendNodes.tenants', 'Verfügbare Tenants')}
            helperText={t('cdaSendNodes.tenantsHelp', 'Leer = für alle Tenants verfügbar')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} className="button-cancel">
            {t('cdaSendNodes.dialog.cancel', 'Abbrechen')}
          </Button>
          <Button onClick={handleSubmit} variant="contained" className="button">
            {editingNode ? t('cdaSendNodes.dialog.update', 'Aktualisieren') : t('cdaSendNodes.dialog.create', 'Erstellen')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ ...confirmDelete, open: false })}
      >
        <DialogTitle>{confirmDelete.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDelete.description}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ ...confirmDelete, open: false })} className="button-cancel">
            {t('cdaSendNodes.confirmDelete.cancel', 'Abbrechen')}
          </Button>
          <Button 
            onClick={() => {
              if (confirmDelete.onConfirm) confirmDelete.onConfirm();
              setConfirmDelete({ ...confirmDelete, open: false });
            }} 
            color="primary" 
            autoFocus
            className="button"
          >
            {t('cdaSendNodes.confirmDelete.confirm', 'Löschen')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CDASendNodes;
